import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, Row, Col, Statistic, Space, message, Spin, DatePicker, Button, Empty, Tooltip } from 'antd';
import {
  TeamOutlined,
  DollarOutlined,
  RiseOutlined,
  UserOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import dayjs from 'dayjs';
import { fetchTeamPerformance, clearError } from '../../features/team/teamSlice';
import { formatAmount, formatNumber, formatDate } from '../../utils/index';
import api from '../../utils/api';

const { RangePicker } = DatePicker;

const TeamPerformance = () => {
  const dispatch = useDispatch();
  const { teamPerformance, isLoading, error } = useSelector((state) => state.team);
  
  const [trendData, setTrendData] = useState([]);
  const [levelDistribution, setLevelDistribution] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState(null);

  useEffect(() => {
    loadData();
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      dispatch(clearError());
    }
  }, [error, dispatch]);

  // 日期范围变化时重新加载数据
  useEffect(() => {
    loadData();
  }, [dateRange]);

  // 加载所有数据
  const loadData = () => {
    let params = {};
    if (dateRange && dateRange[0] && dateRange[1]) {
      params = {
        startDate: dateRange[0].format('YYYY-MM-DD'),
        endDate: dateRange[1].format('YYYY-MM-DD')
      };
    }
    dispatch(fetchTeamPerformance(params));
    loadChartData(params);
  };

  // 加载图表数据
  const loadChartData = async (params) => {
    setLoading(true);
    try {
      const [trendRes, levelRes] = await Promise.all([
        api.get('/admin/team/performance-trend', { params }),
        api.get('/admin/team/level-distribution', { params })
      ]);
      
      setTrendData(trendRes.data || []);
      setLevelDistribution(levelRes.data || []);
    } catch (err) {
      message.error(err.response?.data?.message || '加载图表数据失败');
    } finally {
      setLoading(false);
    }
  };

  // 刷新数据
  const handleRefresh = () => {
    loadData();
    message.success('数据已刷新');
  };



  // 快捷日期选择
  const rangePresets = [
    { label: '最近7天', value: [dayjs().subtract(6, 'days'), dayjs()] },
    { label: '最近30天', value: [dayjs().subtract(29, 'days'), dayjs()] },
    { label: '本月', value: [dayjs().startOf('month'), dayjs()] },
    { label: '上月', value: [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')] },
  ];

  // 团队业绩趋势图表
  const performanceTrendOption = {
    title: {
      text: dateRange && dateRange[0] && dateRange[1] 
        ? `团队业绩趋势（${dateRange[0].format('MM-DD')} 至 ${dateRange[1].format('MM-DD')}）`
        : '团队业绩趋势（全部）',
      left: 'center',
    },
    tooltip: {
      trigger: 'axis',
      formatter: (params) => {
        let result = params[0].name + '<br/>';
        params.forEach(param => {
          result += `${param.seriesName}: ${formatAmount(param.value)}<br/>`;
        });
        return result;
      },
    },
    legend: {
      data: ['团队充值', '返点发放', '净收益'],
      top: 30,
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      containLabel: true
    },
    toolbox: {
      feature: {
        saveAsImage: { title: '保存为图片' },
        dataZoom: { title: { zoom: '区域缩放', back: '还原' } },
        restore: { title: '还原' }
      }
    },
    xAxis: {
      type: 'category',
      data: trendData.map(item => formatDate(item.date, 'MM-DD')),
      boundaryGap: true
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: (value) => {
          if (value >= 1000000) return `¥${(value / 1000000).toFixed(1)}M`;
          if (value >= 1000) return `¥${(value / 1000).toFixed(1)}k`;
          return `¥${value}`;
        },
      },
    },
    series: [
      {
        name: '团队充值',
        type: 'bar',
        data: trendData.map(item => item.recharge),
        itemStyle: { color: '#1890ff' },
        emphasis: { focus: 'series' }
      },
      {
        name: '返点发放',
        type: 'bar',
        data: trendData.map(item => item.commission),
        itemStyle: { color: '#52c41a' },
        emphasis: { focus: 'series' }
      },
      {
        name: '净收益',
        type: 'line',
        data: trendData.map(item => item.netProfit),
        itemStyle: { color: '#722ed1' },
        lineStyle: { width: 3 },
        smooth: true,
        emphasis: { focus: 'series' }
      },
    ],
  };

  // 代理等级分布图表
  const agentLevelDistributionOption = {
    title: {
      text: '代理等级分布',
      left: 'center',
    },
    tooltip: {
      trigger: 'item',
      formatter: '{a} <br/>{b}: {c} ({d}%)',
    },
    legend: {
      orient: 'vertical',
      left: 'left',
      data: levelDistribution.map(item => `Lv.${item.level}`),
    },
    toolbox: {
      feature: {
        saveAsImage: { title: '保存为图片' }
      }
    },
    series: [
      {
        name: '代理等级',
        type: 'pie',
        radius: ['40%', '70%'],
        avoidLabelOverlap: false,
        itemStyle: {
          borderRadius: 10,
          borderColor: '#fff',
          borderWidth: 2
        },
        label: {
          show: true,
          formatter: '{b}: {c}'
        },
        data: levelDistribution.map(item => ({
          value: item.count,
          name: `Lv.${item.level}`
        })),
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.5)',
          },
        },
      },
    ],
  };

  return (
    <Spin spinning={isLoading || loading}>
      <div>
        <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 className="page-title">团队业绩</h1>
            <p className="page-description">团队业绩统计和分析</p>
          </div>
          <Space>
            <RangePicker
              value={dateRange}
              onChange={setDateRange}
              presets={rangePresets}
              format="YYYY-MM-DD"
              placeholder={['开始日期', '结束日期']}
              allowClear
            />
            <Tooltip title="刷新数据">
              <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
                刷新
              </Button>
            </Tooltip>
          </Space>
        </div>

      {/* 统计卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable>
            <Statistic
              title="总代理数"
              value={teamPerformance.totalAgents || 0}
              prefix={<TeamOutlined />}
              valueStyle={{ color: '#1890ff' }}
              formatter={(value) => formatNumber(value)}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable>
            <Statistic
              title="活跃代理"
              value={teamPerformance.activeAgents || 0}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#52c41a' }}
              formatter={(value) => formatNumber(value)}
            />
            <div style={{ marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>
              活跃率: {teamPerformance.totalAgents > 0 
                ? ((teamPerformance.activeAgents / teamPerformance.totalAgents) * 100).toFixed(1) 
                : 0}%
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable>
            <Statistic
              title="团队总规模"
              value={teamPerformance.totalTeamSize || 0}
              prefix={<TeamOutlined />}
              valueStyle={{ color: '#fa8c16' }}
              formatter={(value) => formatNumber(value)}
            />
            <div style={{ marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>
              人均团队: {teamPerformance.totalAgents > 0 
                ? (teamPerformance.totalTeamSize / teamPerformance.totalAgents).toFixed(1) 
                : 0}人
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable>
            <Statistic
              title="平均返点率"
              value={teamPerformance.averageCommissionRate || 0}
              suffix="%"
              prefix={<RiseOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 金额统计 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable>
            <Statistic
              title="团队总充值"
              value={teamPerformance.totalRecharge || 0}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#1890ff' }}
              formatter={(value) => formatAmount(value)}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable>
            <Statistic
              title="总返点发放"
              value={teamPerformance.totalCommission || 0}
              prefix={<RiseOutlined />}
              valueStyle={{ color: '#52c41a' }}
              formatter={(value) => formatAmount(value)}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable>
            <Statistic
              title="净收益"
              value={(teamPerformance.totalRecharge || 0) - (teamPerformance.totalCommission || 0)}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#722ed1' }}
              formatter={(value) => formatAmount(value)}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable>
            <Statistic
              title="实际返点率"
              value={teamPerformance.totalRecharge > 0 ? ((teamPerformance.totalCommission / teamPerformance.totalRecharge) * 100).toFixed(2) : 0}
              suffix="%"
              prefix={<RiseOutlined />}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 图表区域 */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="团队业绩趋势" style={{ height: 450 }}>
            {trendData.length > 0 ? (
              <ReactECharts
                option={performanceTrendOption}
                style={{ height: '360px' }}
                opts={{ renderer: 'svg' }}
              />
            ) : (
              <Empty description="暂无趋势数据" style={{ marginTop: 100 }} />
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="代理等级分布" style={{ height: 450 }}>
            {levelDistribution.length > 0 ? (
              <ReactECharts
                option={agentLevelDistributionOption}
                style={{ height: '360px' }}
                opts={{ renderer: 'svg' }}
              />
            ) : (
              <Empty description="暂无等级分布数据" style={{ marginTop: 100 }} />
            )}
          </Card>
        </Col>
      </Row>
    </div>
    </Spin>
  );
};

export default TeamPerformance;
